import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Columns3, RotateCcw, Check, GripVertical, Info } from 'lucide-react';
import './ReportsNative.css';

/**
 * Column customization control for ReportMatrix / ReportDetailTable.
 * Driven by the `columnPrefs` object from pages/reports/useColumnPrefs:
 * { allColumns, visibleKeys, isCustomized, hasSavedDefault,
 *   toggleColumn, resetToDefault, saveAsDefault, clearSavedDefault }.
 *
 * Renders a "Columns" picker popover plus — whenever the active set differs
 * from the user's default — an orange-hued "Customized · Reset" pill that
 * restores the default in one click.
 *
 * `sections` adds extra checkbox groups above the columns list for choices
 * that aren't columns but belong in the same control — e.g. the P&L's
 * Location row tier, which unchecks into a consolidated statement:
 *   [{ title, items: [{ key, label, description?, checked, onToggle }] }]
 */
export function ColumnPickerControl({ columnPrefs, sections }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [dragOver, setDragOver] = useState(null); // { key, after }
  const [tip, setTip] = useState(null); // { text, x, y } — fixed-position metric explanation
  const [pos, setPos] = useState(null); // fixed-position anchor for the portaled popover
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTip(null);
      return undefined;
    }
    const onDown = (e) => {
      // the popover portals to <body>, so check both the trigger and the pop
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // The popover renders in a <body> portal at position:fixed so nothing —
  // card overflow, table scroll wrappers — can clip it. It sizes to the
  // VIEWPORT: max-height is whatever screen space remains, and it flips
  // upward when the trigger sits near the bottom (e.g. the floating dock).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const GAP = 6;
    const MARGIN = 12;
    const compute = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const spaceBelow = vh - rect.bottom - GAP - MARGIN;
      const spaceAbove = rect.top - GAP - MARGIN;
      const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
      setPos({
        right: Math.max(MARGIN, vw - rect.right),
        top: openUp ? undefined : rect.bottom + GAP,
        bottom: openUp ? vh - rect.top + GAP : undefined,
        maxHeight: Math.max(180, openUp ? spaceAbove : spaceBelow),
      });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  if (!columnPrefs) return null;
  const {
    allColumns,
    visibleKeys,
    isCustomized,
    hasSavedDefault,
    toggleColumn,
    reorderColumns,
    facets,
    resetToDefault,
    saveAsDefault,
    clearSavedDefault,
  } = columnPrefs;

  // Faceted tables (e.g. the P&L Income Statement) collapse the picker to one
  // control per metric — Actual / Budget / Var — that applies across every
  // month group + Total, instead of a row per (group × metric). Flat tables
  // keep the per-column list.
  const faceted = !!facets;

  // Normalize both modes to a single { key, label, checked } item list plus the
  // reorderable "visible sequence" and its toggle/reorder handlers.
  const byKey = new Map(allColumns.map((c) => [c.key, c]));
  const pickerItems = faceted
    ? facets.items
    : [
        ...visibleKeys.map((k) => byKey.get(k)).filter(Boolean),
        ...allColumns.filter((c) => !visibleKeys.includes(c.key)),
      ].map((c) => ({
        key: c.key,
        label: c.group ? `${c.group} · ${c.label}` : c.label,
        description: c.description || null,
        checked: visibleKeys.includes(c.key),
      }));
  const visibleSeq = faceted ? facets.visibleFacets : visibleKeys;
  const onToggleItem = faceted ? facets.toggleFacet : toggleColumn;
  const onReorderVisible = faceted ? facets.reorderFacets : reorderColumns;

  const handleSave = async (fn) => {
    setSaving(true);
    try {
      await fn();
    } catch (e) {
      // surfaced via console; picker stays open so the user can retry
      console.error('Failed to update column preferences:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="rn-colpicker" ref={wrapRef}>
      {isCustomized && (
        <button
          type="button"
          className="rn-customized-pill"
          title="Columns differ from your default — click to reset"
          onClick={resetToDefault}
        >
          <RotateCcw />
          Customized · Reset
        </button>
      )}
      <button
        type="button"
        className={`rn-icon-btn${open ? ' rn-icon-btn-active' : ''}`}
        title="Choose columns"
        onClick={() => setOpen((v) => !v)}
      >
        <Columns3 />
      </button>
      {open && pos && createPortal(
        <div
          className="rn-colpicker-pop rn-colpicker-pop-fixed"
          role="menu"
          ref={popRef}
          style={{
            position: 'fixed',
            top: pos.top,
            bottom: pos.bottom,
            right: pos.right,
            maxHeight: pos.maxHeight,
          }}
        >
          {(sections || []).map((section) => (
            <React.Fragment key={section.title}>
              <div className="rn-colpicker-head">{section.title}</div>
              <div className="rn-colpicker-list">
                {section.items.map((item) => (
                  <div key={item.key} className="rn-colpicker-item">
                    <GripVertical className="rn-colpicker-grip rn-colpicker-grip-off" aria-hidden="true" style={{ visibility: 'hidden' }} />
                    <label className="rn-colpicker-label">
                      <span className={`rn-colpicker-box${item.checked ? ' rn-colpicker-box-on' : ''}`}>
                        {item.checked && <Check />}
                      </span>
                      <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => item.onToggle(!item.checked)}
                        style={{ display: 'none' }}
                      />
                      {item.label}
                    </label>
                    {item.description && (
                      <span
                        className="rn-colinfo"
                        aria-label={`What ${item.label} does`}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTip({ text: item.description, x: rect.left, y: rect.bottom + 6 });
                        }}
                        onMouseLeave={() => setTip(null)}
                        onClick={(e) => e.preventDefault()}
                      >
                        <Info />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
          <div className="rn-colpicker-head">Columns</div>
          <div className="rn-colpicker-list">
            {pickerItems.map((item) => {
              const checked = item.checked;
              const isDragSource = dragKey === item.key;
              const dropHere = dragOver?.key === item.key && dragKey && dragKey !== item.key;
              return (
                <div
                  key={item.key}
                  className={[
                    'rn-colpicker-item',
                    checked ? 'rn-colpicker-item-draggable' : '',
                    isDragSource ? 'rn-colpicker-item-dragging' : '',
                    dropHere ? (dragOver.after ? 'rn-drop-after' : 'rn-drop-before') : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  draggable={checked}
                  onDragStart={checked ? (e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    try { e.dataTransfer.setData('text/plain', item.key); } catch (_) { /* IE */ }
                    setDragKey(item.key);
                  } : undefined}
                  onDragOver={checked ? (e) => {
                    if (!dragKey || dragKey === item.key) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    const rect = e.currentTarget.getBoundingClientRect();
                    const after = e.clientY > rect.top + rect.height / 2;
                    setDragOver((cur) => (cur?.key === item.key && cur.after === after ? cur : { key: item.key, after }));
                  } : undefined}
                  onDrop={checked ? (e) => {
                    e.preventDefault();
                    if (dragKey && dragKey !== item.key) {
                      const next = visibleSeq.filter((k) => k !== dragKey);
                      const idx = next.indexOf(item.key) + (dragOver?.after ? 1 : 0);
                      next.splice(idx, 0, dragKey);
                      onReorderVisible(next);
                    }
                    setDragKey(null);
                    setDragOver(null);
                  } : undefined}
                  onDragEnd={() => {
                    setDragKey(null);
                    setDragOver(null);
                  }}
                >
                  <GripVertical className={`rn-colpicker-grip${checked ? '' : ' rn-colpicker-grip-off'}`} aria-hidden="true" />
                  <label className="rn-colpicker-label">
                    <span className={`rn-colpicker-box${checked ? ' rn-colpicker-box-on' : ''}`}>
                      {checked && <Check />}
                    </span>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleItem(item.key)}
                      style={{ display: 'none' }}
                    />
                    {item.label}
                  </label>
                  {item.description && (
                    <span
                      className="rn-colinfo"
                      aria-label={`How ${item.label} is calculated`}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTip({ text: item.description, x: rect.left, y: rect.bottom + 6 });
                      }}
                      onMouseLeave={() => setTip(null)}
                      onClick={(e) => e.preventDefault()}
                    >
                      <Info />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {tip && (
            <div className="rn-coltip" style={{ left: Math.max(8, tip.x - 220), top: tip.y }} role="tooltip">
              {tip.text}
            </div>
          )}
          <div className="rn-colpicker-foot">
            <button
              type="button"
              className="rn-btn rn-btn-xs"
              disabled={saving || !isCustomized}
              onClick={() => handleSave(saveAsDefault)}
            >
              {saving ? 'Saving…' : 'Save as my default'}
            </button>
            <button type="button" className="rn-btn rn-btn-xs" disabled={!isCustomized} onClick={resetToDefault}>
              Reset
            </button>
            {hasSavedDefault && (
              <button
                type="button"
                className="rn-btn rn-btn-xs rn-btn-quiet"
                disabled={saving}
                onClick={() => handleSave(clearSavedDefault)}
                title="Remove your saved default (back to all columns)"
              >
                Clear saved
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

/**
 * Filter AND order a columns config by columnPrefs.visibleKeys
 * (kit-internal) — the visibleKeys sequence is the render order.
 */
export function applyColumnPrefs(columns, columnPrefs) {
  if (!columnPrefs || !Array.isArray(columns)) return columns;
  const { visibleKeys } = columnPrefs;
  if (!Array.isArray(visibleKeys) || visibleKeys.length === 0) return columns;
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const ordered = visibleKeys.map((k) => byKey.get(k)).filter(Boolean);
  return ordered.length > 0 ? ordered : columns;
}

export default ColumnPickerControl;
